/**
 * 勤務表の日ごとの「入力区分」（打刻・打刻後修正・手入力）。
 *
 * **本人の勤務表・承認者が開く勤務表・申請の確認は、全部ここを通す。**
 * 入口ごとに判定を書くと、同じ日が画面によって別の入力区分に見える。
 *
 * ## 根拠があるときだけ断定する（監査 STAMP-3）
 *
 * 記録は2種類ある（どちらも、その日の記録の並びに、書かれた順で入っている）。
 * - 打刻の記録（START・END）：打刻が勤務表へ書いた時刻つき。
 * - 勤務表で変えた記録（ROSTER_START・ROSTER_END）：勤務表の保存で、その欄の時刻が変わったときに残る。
 *
 * **打刻の記録が無いことは、勤務表で入力した証拠にしない。**機能を入れた当日に、導入の前に
 * 打った出勤・記録が欠けた打刻は、記録が無い。そういう欄を「手入力」「修正」と出すと、
 * 打刻したままの人が、時刻を書き換えたように見える。
 *
 * 欄（開始・終了）ごとに、次の順で決める。
 * 1. いま画面の値が、読み込んだ値（保存されている値）と違う → 勤務表で入れた欄（いま書き換えている）。
 * 2. その欄の最後の記録が「勤務表で変えた記録」→ 勤務表で入れた欄。
 * 3. その欄の最後の記録が「打刻の記録」→ 時刻が打刻のままなら打刻、違えば勤務表で入れた欄
 *    （打刻した時刻と違う値が入っていること自体が根拠）。
 * 4. その欄の記録が無い → **何も断定しない**（濃くしない・数えない）。
 *
 * 日の入力区分：
 * - 勤務表で入れた欄が1つでもある → その日に打刻の記録があれば「打刻後修正」、無ければ「手入力」。
 * - 勤務表で入れた欄が無い → 打刻の記録があれば「打刻」、無ければ何も出さない。
 *
 * 比べるのは出勤と退勤だけ。休憩は、打刻が時間を決めるのでなく勤務表の計算が決める。
 */

export const ORIGIN_STAMP = 'STAMP'
export const ORIGIN_EDITED = 'EDITED'
export const ORIGIN_ROSTER = 'ROSTER'

const TIME_PATTERN = /^(\d{1,2}):(\d{2})/

/**
 * 時刻を「HH:mm」へそろえる。空・読めない値は空文字。
 * 日をまたぐ書き方（30:00）は、時計の時刻（06:00）へ直して比べる。
 */
export const normalizedClock = value => {
  const match = TIME_PATTERN.exec(value == null ? '' : String(value).trim())
  if (!match) return ''
  const hours = Number(match[1]) % 24
  return String(hours).padStart(2, '0') + ':' + match[2]
}

const emptyField = () => ({ latest: '', stampTime: '', hasStamp: false })

/** その記録が、打刻（出勤・退勤・休憩）の記録か。勤務表で変えた記録は false。 */
export const isStampEvent = event =>
  !!event && ['START', 'END', 'BREAK_START', 'BREAK_END'].includes(event.event_kind)

/**
 * 記録を勤務日ごとにまとめる。欄ごとに、最後の記録の種類（打刻か、勤務表で変えたか）と、
 * 最後に打った時刻を持つ（打ち直した出勤は、勤務表にも最後の時刻が入っている）。
 */
export const stampEventsByDate = events => {
  const byDate = {}
  ;(Array.isArray(events) ? events : []).forEach(event => {
    if (!event || !event.work_date) return
    const day = byDate[event.work_date] ||
      (byDate[event.work_date] = { start: emptyField(), end: emptyField(), events: [] })
    day.events.push(event)
    const kind = event.event_kind
    if (kind === 'START' || kind === 'END') {
      const field = kind === 'START' ? day.start : day.end
      field.latest = 'stamp'
      field.stampTime = normalizedClock(event.stamped_time)
      field.hasStamp = true
    } else if (kind === 'ROSTER_START' || kind === 'ROSTER_END') {
      const field = kind === 'ROSTER_START' ? day.start : day.end
      field.latest = 'roster'
    }
  })
  return byDate
}

/** その欄が、勤務表で入れた欄か（根拠があるときだけ true）。 */
const fieldByRoster = (field, current, saved) => {
  const now = normalizedClock(current)
  const loaded = saved === undefined ? now : normalizedClock(saved)
  if (now !== loaded) return true
  if (!field) return false
  if (field.latest === 'roster') return true
  if (field.latest === 'stamp') return now !== field.stampTime
  return false
}

/**
 * その日の入力区分。
 *
 * @param {object} input
 * @param {object} input.stamp stampEventsByDate のその日の分（無ければ undefined）
 * @param {string} input.start いま画面にある開始時刻
 * @param {string} input.end いま画面にある終了時刻
 * @param {string} [input.savedStart] 読み込んだ（保存されている）開始時刻。省くと、いまの値と同じとみなす
 * @param {string} [input.savedEnd] 読み込んだ終了時刻
 * @returns {null|{origin: string, startByRoster: boolean, endByRoster: boolean}}
 */
export const stampOriginOfDay = ({ stamp, start, end, savedStart, savedEnd }) => {
  const startByRoster = fieldByRoster(stamp && stamp.start, start, savedStart)
  const endByRoster = fieldByRoster(stamp && stamp.end, end, savedEnd)
  const hasStamp = !!stamp && (stamp.start.hasStamp || stamp.end.hasStamp)

  if (startByRoster || endByRoster) {
    return { origin: hasStamp ? ORIGIN_EDITED : ORIGIN_ROSTER, startByRoster, endByRoster }
  }
  return hasStamp ? { origin: ORIGIN_STAMP, startByRoster: false, endByRoster: false } : null
}

/**
 * 月の全日分。dates・starts・ends・savedStarts・savedEnds は同じ並び（勤務表の行の順）。
 */
export const stampOriginsOfMonth = ({ dates, events, starts, ends, savedStarts, savedEnds }) => {
  const byDate = stampEventsByDate(events)
  return (Array.isArray(dates) ? dates : []).map((date, index) => stampOriginOfDay({
    stamp: byDate[date],
    start: starts ? starts[index] : '',
    end: ends ? ends[index] : '',
    savedStart: savedStarts ? savedStarts[index] : undefined,
    savedEnd: savedEnds ? savedEnds[index] : undefined
  }))
}

/** 勤務表で入力・修正した日（打刻後修正＋手入力）の、行の番号の一覧。 */
export const rosterEnteredDayIndexes = origins =>
  (Array.isArray(origins) ? origins : [])
    .map((origin, index) => (origin && origin.origin !== ORIGIN_STAMP ? index : -1))
    .filter(index => index >= 0)

/** 地図を開く先。緯度・経度が数でなければ空文字。 */
export const mapUrlOf = event => {
  if (!event || event.location_status !== 'OK') return ''
  const latitude = Number(event.latitude)
  const longitude = Number(event.longitude)
  if (!isFinite(latitude) || !isFinite(longitude)) return ''
  if (event.latitude == null || event.longitude == null) return ''
  return 'https://www.google.com/maps?q=' + latitude + ',' + longitude
}

/**
 * 端末の時計で打った時刻と、サーバが受け取った時刻の差（分）。読めなければ null。
 * 打刻の時刻は端末の時計から取るので、時計を動かして打つと、この差が大きくなる。
 */
export const clockGapMinutes = event => {
  if (!event || !event.event_at || !event.received_at) return null
  const parse = text => {
    const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.exec(String(text))
    return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) : NaN
  }
  const gap = (parse(event.received_at) - parse(event.event_at)) / 60000
  return isFinite(gap) ? Math.round(gap) : null
}
